import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Mail, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

/**
 * Forgot-password dialog opened from the LoginPage. Sends a request to
 * POST /api/auth/forgot-password — the backend always returns 200 with
 * a generic message regardless of whether the email exists, so we never
 * leak account enumeration. UI mirrors that with a single success state.
 */
export default function ForgotPasswordDialog({ trigger }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!email.trim()) return;
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim() });
      setSent(true);
      toast.success(t("forgot.toast_sent"));
    } catch (e2) {
      // Backend returns 200 even on bad email; this branch only fires on
      // network failure or 5xx.
      toast.error(
        formatApiErrorDetail(e2.response?.data?.detail) || t("forgot.toast_error"),
      );
    } finally {
      setBusy(false);
    }
  };

  const reset = () => {
    setEmail("");
    setSent(false);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (!o) reset();
      }}
    >
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="text-sm text-orange-500 hover:text-orange-400 transition underline-offset-4 hover:underline"
        data-testid="login-forgot-password-link"
      >
        {trigger || t("forgot.trigger")}
      </button>

      <DialogContent
        className="bg-zinc-950 border-orange-500/30 max-w-md"
        data-testid="forgot-password-dialog"
      >
        <DialogHeader>
          <DialogTitle className="text-white font-outfit flex items-center gap-2">
            <Mail className="text-orange-400" size={18} />
            {t("forgot.title")}
          </DialogTitle>
          <DialogDescription className="text-zinc-400 text-sm">
            {t("forgot.description")}
          </DialogDescription>
        </DialogHeader>

        {sent ? (
          <div className="py-6 text-center">
            <div className="mx-auto w-12 h-12 rounded-full bg-emerald-500/20 flex items-center justify-center mb-3">
              <CheckCircle2 className="text-emerald-400" size={22} />
            </div>
            <p className="text-emerald-300 font-medium">{t("forgot.sent_title")}</p>
            <p className="text-xs text-zinc-500 mt-1 leading-relaxed">
              {t("forgot.sent_body")}
            </p>
            <Button
              onClick={() => setOpen(false)}
              className="mt-5 bg-orange-500 hover:bg-orange-600 text-white"
              data-testid="forgot-password-close"
            >
              {t("forgot.sent_close")}
            </Button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label className="text-zinc-300 text-xs uppercase tracking-[0.2em]">
                {t("forgot.email_label")}
              </Label>
              <Input
                autoFocus
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t("forgot.email_placeholder")}
                className="bg-zinc-900/50 border-white/10 mt-2 h-12"
                data-testid="forgot-password-email"
                required
              />
            </div>
            <Button
              type="submit"
              disabled={busy || !email.trim()}
              className="w-full h-12 bg-orange-500 hover:bg-orange-600 text-white"
              data-testid="forgot-password-submit"
            >
              {busy ? (
                <>
                  <Loader2 size={16} className="mr-2 animate-spin" />
                  {t("forgot.submit_loading")}
                </>
              ) : (
                <>
                  <Mail size={14} className="mr-2" />
                  {t("forgot.submit_btn")}
                </>
              )}
            </Button>
            <p className="text-[10px] text-zinc-600 text-center leading-relaxed">
              {t("forgot.rate_limit_note")}
            </p>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
